// Globals
import React from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { StyledButtonWrap, StyledForm, StyledFormItem, StyledSelect } from './CreateQrStyles';
import { FormItem, IFormInstance } from '@/components';
import { Button, Select } from '@/ui';

// Modules
import { getCategoriesOptions, useCategoriesAutocomplete } from '@/modules/Categories';
import { useAutocompleteRegions } from '@/modules/Regions/hooks';
import { usePartnersListSearch } from '@/modules/Partners';
import { getProductList } from '@/modules/Products/helpers';

// Models
import { IProduct } from '@/models/products.model';

// Helpers
import { GenerateQrFields, getQrTypesOptions, TGenerateQrFormValues, getPartnersOptions } from '../../helpers';
import { useGenerateQrFormValidationRules } from '../../hooks';

type TCreateQr = {
  formInstance: IFormInstance;
  productsList?: IProduct[];
  onSubmit: (values: TGenerateQrFormValues) => Promise<void>;
  debouncedFetchProducts: (searchText: string) => void;
  isLoading: boolean;
};

const CreateQr = ({ formInstance, isLoading, productsList, debouncedFetchProducts, onSubmit }: TCreateQr) => {
  const { t } = useTranslation(['products']);
  const rules = useGenerateQrFormValidationRules();
  const { list: categoriesList, onSearch: onCategoriesSearch } = useCategoriesAutocomplete({});
  const { onSearch: onSearchRegions, regionsOptions } = useAutocompleteRegions();
  const { partnersList, searchPartners } = usePartnersListSearch();

  const handleProductsSearch = (value: string) => {
    debouncedFetchProducts(value);
  };

  return (
    <StyledForm form={formInstance} onFinish={onSubmit}>
      <StyledFormItem name={GenerateQrFields.STORES} label={t('generate_qr_create_partner_label')}>
        <StyledSelect
          allowClear
          showSearch
          onSearch={searchPartners}
          options={getPartnersOptions(partnersList ?? [])}
          isMultiselect
          maxTagCount={100}
        />
      </StyledFormItem>
      <StyledFormItem name={GenerateQrFields.CATEGORIES} label={t('generate_qr_create_category_label')}>
        <StyledSelect
          allowClear
          showSearch
          options={getCategoriesOptions(categoriesList ?? [])}
          onSearch={onCategoriesSearch}
          isMultiselect
          maxTagCount={100}
        />
      </StyledFormItem>
      <StyledFormItem name={GenerateQrFields.PRODUCTS} label={t('generate_qr_create_products_label')}>
        <StyledSelect
          allowClear
          showSearch
          options={getProductList(productsList)}
          onSearch={handleProductsSearch}
          isMultiselect
          maxTagCount={100}
        />
      </StyledFormItem>
      <FormItem
        name={GenerateQrFields.REGION_ID}
        label={t('generate_qr_create_region_label')}
        rules={rules[GenerateQrFields.REGION_ID]}
      >
        <Select
          filterOption={false}
          options={regionsOptions ?? []}
          size="middle"
          onSearch={onSearchRegions}
          allowClear
          showSearch
          placeholder={t('generate_qr_region_placeholder')}
        />
      </FormItem>
      <FormItem
        name={GenerateQrFields.TYPE}
        label={t('generate_qr_create_type_label')}
        rules={rules[GenerateQrFields.TYPE]}
      >
        <Select options={getQrTypesOptions(t)} />
      </FormItem>
      <StyledButtonWrap>
        <Button type="primary" htmlType="submit" disabled={isLoading} loading={isLoading}>
          {t('generate_qr_create_title')}
        </Button>
      </StyledButtonWrap>
    </StyledForm>
  );
};

export default CreateQr;
